import {
	ConversationsScreen,
	ConversationsSkeleton,
} from "@commerce/conversations/ConversationsScreen";
import {
	conversationsSearchSchema,
	listInputFrom,
} from "@commerce/conversations/meta";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/conversations",
)({
	validateSearch: conversationsSearchSchema,
	staticData: { fullBleed: true },
	// Prefetch the view the URL asks for once, on entry. Filter changes after
	// that are client queries (the screen defers them), not loader runs.
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const search = conversationsSearchSchema.parse(location.search);
		const queryClient = getServerQueryClient();
		await Promise.all([
			queryClient.ensureQueryData(
				orpc.conversations.list.queryOptions({
					input: listInputFrom(organizationId, search),
				}),
			),
			queryClient.ensureQueryData(
				orpc.conversations.facets.queryOptions({
					input: { organizationId },
				}),
			),
			search.id
				? queryClient
						.ensureQueryData(
							orpc.conversations.get.queryOptions({
								input: { conversationId: search.id },
							}),
						)
						.catch(() => null)
				: null,
		]);
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Inbox - ${config.appName}` }] }),
	component: ConversationsPage,
});

function ConversationsPage() {
	const { organizationId, dehydratedState } = Route.useLoaderData();
	return (
		<AsyncBoundary
			fallback={<ConversationsSkeleton />}
			dehydratedState={dehydratedState}
		>
			<ConversationsScreen organizationId={organizationId} />
		</AsyncBoundary>
	);
}
